<!-- ----------------------------------------- template ----------------------------------------- -->
<template>
  <div>
    <div class="todos-intro" style="margin-bottom: 24px; padding: 16px; background-color: #f5f7fa; border-radius: 4px;">
      <h3 style="margin: 0 0 12px 0; color: #303133;">Club Tasks & Todos</h3>
      <p style="margin: 0; color: #606266; line-height: 1.6;">
        This section helps clubs organize and track their tasks effectively. Create, assign, and monitor todos to keep your club activities running smoothly. Members can collaborate by commenting on tasks, marking them complete, and staying updated on their responsibilities. Whether it's planning events, managing projects, or coordinating activities, the todo system ensures clear communication and accountability within your club.
      </p>
    </div>

    <div class="todos-container">
      <div class="todos-list" :class="{ 'with-form': showNewTodoForm, 'with-detail': selectedTodoId }">
        <div class="todos-header">
          <el-button type="primary" size="small" @click="showNewTodoForm = true">
            New Todo
          </el-button>
        </div>

        <div class="todos-grid" v-loading="isLoading">
          <empty-state
            v-if="!isLoading && (!todoList || todoList.length === 0)"
            message="No todos yet"
            description="Click 'New Todo' to create your first task!"
          ></empty-state>

          <div v-else 
               v-for="todo in todoList" 
               :key="todo._id.$id" 
               class="todo-card"
               :class="{ 'selected': selectedTodoId === todo._id.$id }">
            <div class="todo-card-content" @click.stop="handleTodoClick(todo)">
              <div class="todo-card-header">
                <div class="todo-title-wrapper">
                  <el-checkbox
                    v-model="todo.status"
                    :true-label="'completed'"
                    :false-label="'pending'"
                    @change="(val) => handleStatusChange(todo, val)"
                    @click.stop
                    :disabled="!canModifyTodo(todo)"
                  ></el-checkbox>
                  <h3 class="todo-title" :class="{ 'completed': todo.status === 'completed' }">
                    {{ todo.title }}
                  </h3>
                </div>
                <el-tag :type="getStatusType(todo.status)" size="small">
                  {{ todo.status }}
                </el-tag>
              </div>

              <div class="todo-description">
                {{ todo.desc }}
              </div>

              <div class="todo-meta">
                <div class="todo-assignee">
                  <i class="el-icon-user"></i>
                  <span>{{ todo.charge_user ? formatName(todo.charge_user) : 'Unassigned' }}</span>
                </div>
                <div class="todo-due-date">
                  <i class="el-icon-time"></i>
                  <span>{{ formatDate(todo.date) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Todo Detail View -->
      <div v-if="selectedTodoId && !showNewTodoForm" class="todo-detail">
        <div class="detail-header">
          <h2>Todo Details</h2>
          <div class="detail-actions">
            <el-button type="primary" size="small" @click="handleEdit(selectedTodo)">
              <i class="el-icon-edit"></i> Edit
            </el-button>
            <el-button 
              v-if="selectedTodo.creator_id === currentUserId"
              type="danger" 
              size="small" 
              @click="handleDelete(selectedTodo)"
            >
              <i class="el-icon-delete"></i> Delete
            </el-button>
            <el-button type="text" @click="selectedTodoId = null">
              <i class="el-icon-close"></i>
            </el-button>
          </div>
        </div>

        <div class="detail-content" v-if="selectedTodo">
          <div class="detail-section">
            <div class="detail-title-wrapper">
              <el-checkbox
                v-model="selectedTodo.status"
                :true-label="'completed'"
                :false-label="'pending'"
                @change="(val) => handleStatusChange(selectedTodo, val)"
                :disabled="!canModifyTodo(selectedTodo)"
              ></el-checkbox>
              <h3 :class="{ 'completed': selectedTodo.status === 'completed' }">
                {{ selectedTodo.title }}
              </h3>
            </div>
            <div class="detail-section description-section">
            
              <p>{{ selectedTodo.desc }}</p>
            </div>
            <el-tag :type="getStatusType(selectedTodo.status)" size="small">
              {{ selectedTodo.status }}
            </el-tag>
          </div>

          <div class="detail-meta">
            <div class="meta-item">
              <i class="el-icon-user"></i>
              <div class="meta-content">
                <label>Assignee</label>
                <span>{{ selectedTodo.charge_user ? formatName(selectedTodo.charge_user) : 'Unassigned' }}</span>
              </div>
            </div>

            <div class="meta-item">
              <i class="el-icon-time"></i>
              <div class="meta-content">
                <label>Due Date</label>
                <span>{{ formatDate(selectedTodo.date) }}</span>
              </div>
            </div>

            <div class="meta-item">
              <i class="el-icon-edit"></i>
              <div class="meta-content">
                <label>Created by</label>
                <span>{{ selectedTodo.creator_name }}</span>
              </div>
            </div>
          </div>

          <div class="messages-section">
            <h3>Comments</h3>
            <div class="messages-list">
              <div v-if="todoMessages && todoMessages.length > 0">
                <div v-for="message in todoMessages" :key="message._id.$id" class="message-item">
                  <div class="message-meta">
                    <span class="message-author">{{ formatMessageAuthor(message) }}</span>
                    <span class="message-time">{{ formatDate(message.create_time) }}</span>
                  </div>
                  <div class="message-content">
                    {{ message.content }}
                  </div>
                </div>
              </div>
              <div v-else class="no-messages">
                No comments yet. Be the first to comment!
              </div>
            </div>
            <div class="new-comment" v-if="getUserType() !== 'schoolAdmin'">
              <el-input
                type="textarea"
                v-model="newTodoComment"
                :rows="3"
                placeholder="Write a comment..."
              ></el-input>
              <div class="comment-actions">
                <el-button type="primary" size="small" @click="submitTodoComment">Comment</el-button>
              </div>
            </div>
            <div v-else class="admin-no-comment">
              School administrators cannot leave comments.
            </div>
          </div>
        </div>
      </div>

      <!-- New/Edit Todo Form -->
      <div v-if="showNewTodoForm" class="todo-form">
        <div class="form-header">
          <h3>{{ isEditing ? 'Edit Todo' : 'New Todo' }}</h3>
          <el-button type="text" @click="handleFormClose">
            <i class="el-icon-close"></i>
          </el-button>
        </div>

        <el-form :model="newTodo" :rules="rules" ref="todoForm" label-width="100px">
          <el-form-item label="Title" prop="title">
            <el-input v-model="newTodo.title" placeholder="Enter todo title"></el-input>
          </el-form-item>

          <el-form-item label="Description" prop="description">
            <el-input
              type="textarea"
              v-model="newTodo.description"
              :rows="4"
              placeholder="Enter todo description"
            ></el-input>
          </el-form-item>

          <el-form-item label="Assignee" prop="charge_user_id">
            <el-select 
              v-model="newTodo.charge_user_id" 
              placeholder="Select assignee"
              style="width: 100%">
              <el-option
                v-for="member in clubMembers"
                :key="member._id.$id"
                :label="formatName(member.user) + (member.user._id.$id === currentUserId ? ' (myself)' : '')"
                :value="member.user._id.$id">
              </el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="Due Date" prop="due_date">
            <el-date-picker
              v-model="newTodo.due_date"
              type="datetime"
              placeholder="Select due date"
              style="width: 100%">
            </el-date-picker>
          </el-form-item>

          
        </el-form>

        <div class="form-footer">
          <el-button @click="handleFormClose">Cancel</el-button>
          <el-button type="primary" @click="submitTodo">{{ isEditing ? 'Save' : 'Create' }}</el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<!-- ------------------------------------------ script ------------------------------------------ -->
<script>
import { postRequest } from '@/api/eclub'
import { getUserId, getUserType } from '@/utils/store'
import EmptyState from '@/components/EmptyState.vue'


export default {
  name: 'Todos',
  components: {
    EmptyState
  },
  props: {
    clubInfo: {
      type: Object,
      default: () => ({})
    },
    club_id: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      todoList: [],
      isLoading: false,
      error: null,
      showNewTodoForm: false,
      newTodo: {
        title: '',
        description: '',
        charge_user_id: '',
        due_date: '',
        club_id: this.club_id
      },
      rules: {
        title: [
          { required: true, message: 'Please enter title', trigger: 'blur' },
          { min: 3, max: 50, message: 'Length should be 3 to 50 characters', trigger: 'blur' }
        ],
        description: [
          { required: true, message: 'Please enter description', trigger: 'blur' }
        ],
        charge_user_id: [
          { required: true, message: 'Please select assignee', trigger: 'change' }
        ],
        due_date: [
          { required: true, message: 'Please select due date', trigger: 'change' }
        ],
        status: [
          { required: true, message: 'Please select status', trigger: 'change' }
        ]
      },
      clubMembers: [],
      currentUserId: getUserId(),
      selectedTodoId: null,
      selectedTodo: null,
      isEditing: false,
      editingTodoId: null,
      todoMessages: [],
      newTodoComment: '',
    }
  },

  created() {
    this.fetchTodoList()
    this.fetchClubMembers()
  },

  methods: {
    getUserType() {
      return getUserType();
    },
    async fetchTodoList() {
      try {
        this.isLoading = true
        const response = await postRequest('getClubTodoList', {
          club_id: this.club_id
        })
        
        if (response.data.code === 0) {
          this.todoList = response.data.data
        } else {
          this.$message.error('Failed to fetch todo list')
        }
      } catch (error) {
        console.error('Error fetching todo list:', error)
        this.$message.error('Failed to fetch todo list')
      } finally {
        this.isLoading = false
      }
    },

    formatName(user) {
      if (!user) return '-'
      return `${user.student_givenName.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')} ${user.student_lastName.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}`
    },

    formatDate(timestamp) {
      if (!timestamp) return '-'
      const date = new Date(timestamp * 1000)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    getStatusType(status) {
      const types = {
        'pending': 'warning',
        'in_progress': 'primary',
        'completed': 'success',
        'cancelled': 'info'
      }
      return types[status] || 'info'
    },

    openNewTodoDialog() {
      // TODO: Implement new todo dialog
      this.$message.info('Coming soon: Add new todo')
    },

    handleEdit(todo) {
      this.isEditing = true;
      this.editingTodoId = todo._id.$id;
      this.showNewTodoForm = true;
      
      this.newTodo = {
        title: todo.title,
        description: todo.desc,
        charge_user_id: todo.charge_user_id,
        due_date: todo.date ? new Date(todo.date * 1000) : '',
        status: todo.status || 'pending',
        club_id: this.club_id
      };
    },

    handleFormClose() {
      this.showNewTodoForm = false;
      this.isEditing = false;
      this.editingTodoId = null;
      this.resetForm();
    },

    handleDelete(todo) {
      this.$confirm(
        'Are you sure you want to delete this todo?',
        'Delete Todo',
        {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }
      ).then(async () => {
        try {
          const response = await postRequest('deleteClubTodo', {
            todo_id: todo._id.$id
          });

          if (response.data.code === 0) {
            this.$message.success('Todo deleted successfully');
            
            // If we're deleting the currently selected todo, clear the selection
            if (this.selectedTodoId === todo._id.$id) {
              this.selectedTodoId = null;
              this.selectedTodo = null;
            }
            
            // Refresh the todo list
            await this.fetchTodoList();
          } else {
            this.$message.error(response.data.message || 'Failed to delete todo');
          }
        } catch (error) {
          console.error('Error deleting todo:', error);
          this.$message.error('Failed to delete todo');
        }
      }).catch(() => {
        // User cancelled the deletion
        this.$message.info('Delete cancelled');
      });
    },

    async submitTodo() {
      try {
        await this.$refs.todoForm.validate();
        
        const todoData = {
          club_id: this.club_id,
          id: this.isEditing ? this.editingTodoId : '',
          user_id: this.currentUserId,
          user_type: getUserType(),
          title: this.newTodo.title,
          desc: this.newTodo.description,
          attachement: [],
          date: this.newTodo.due_date ? Math.floor(new Date(this.newTodo.due_date).getTime() / 1000) : '',
          charge_user_id: this.newTodo.charge_user_id,
          status: this.newTodo.status
        };

        const response = await postRequest('uploadClubTodo', todoData);
        
        if (response.data.code === 0) {
          this.$message.success(this.isEditing ? 'Todo updated successfully' : 'Todo created successfully');
          this.showNewTodoForm = false;
          this.isEditing = false;
          this.editingTodoId = null;
          this.resetForm();
          await this.fetchTodoList();
          
          if (this.selectedTodoId) {
            const updatedTodo = this.todoList.find(t => t._id.$id === this.selectedTodoId);
            if (updatedTodo) {
              this.selectedTodo = updatedTodo;
            }
          }
        } else {
          this.$message.error(response.data.message || `Failed to ${this.isEditing ? 'update' : 'create'} todo`);
        }
      } catch (error) {
        console.error('Error saving todo:', error);
        this.$message.error(`Failed to ${this.isEditing ? 'update' : 'create'} todo`);
      }
    },

    resetForm() {
      if (this.$refs.todoForm) {
        this.$refs.todoForm.resetFields();
      }
      this.newTodo = {
        title: '',
        description: '',
        charge_user_id: '',
        due_date: '',
        status: 'pending',
        club_id: this.club_id
      };
    },

    async fetchClubMembers() {
      try {
        const response = await postRequest('getClubStudents', {
          club_id: this.club_id
        })
        
        if (response.data.code === 0) {
          this.clubMembers = response.data.data
        } else {
          this.$message.error('Failed to fetch club members')
        }
      } catch (error) {
        console.error('Error fetching club members:', error)
        this.$message.error('Failed to fetch club members')
      }
    },

    handleTodoClick(todo) {
      if (this.showNewTodoForm) {
        return;
      }
      
      if (this.selectedTodoId === todo._id.$id) {
        this.selectedTodoId = null;
        this.selectedTodo = null;
      } else {
        this.selectedTodoId = todo._id.$id;
        this.selectedTodo = todo;
        this.fetchTodoMessages(todo._id.$id); // Fetch messages when todo is selected
      }
    },

    async handleStatusChange(todo, newStatus) {
      try {
        const response = await postRequest('changeClubTodoStatus', {
          todo_id: todo._id.$id,
          status: newStatus
        });
        
        if (response.data.code === 0) {
          this.$message.success(`Todo marked as ${newStatus}`);
          await this.fetchTodoList();
          
          // Update selected todo if it's the one being modified
          if (this.selectedTodoId === todo._id.$id) {
            const updatedTodo = this.todoList.find(t => t._id.$id === this.selectedTodoId);
            if (updatedTodo) {
              this.selectedTodo = updatedTodo;
            }
          }
        } else {
          this.$message.error(response.data.message || 'Failed to update todo status');
          // Revert the checkbox state
          todo.status = todo.status === 'completed' ? 'pending' : 'completed';
        }
      } catch (error) {
        console.error('Error updating todo status:', error);
        this.$message.error('Failed to update todo status');
        // Revert the checkbox state
        todo.status = todo.status === 'completed' ? 'pending' : 'completed';
      }
    },

    canModifyTodo(todo) {
      return todo.creator_id === this.currentUserId || todo.charge_user_id === this.currentUserId;
    },

    async fetchTodoMessages(todo_id) {
      try {
        const response = await postRequest('getMessages', {
          from_id: todo_id,
          type: 'todo'
        });
        
        if (response.data.code === 0) {
          this.todoMessages = response.data.data;
        } else if (response.data.code === -1) {
          this.$message.error('Todo not found');
          this.selectedTodoId = null;
          this.selectedTodo = null;
        } else {
          this.$message.error(response.data.message || 'Failed to fetch comments');
        }
      } catch (error) {
        console.error('Error fetching comments:', error);
        this.$message.error('Failed to fetch comments');
      }
    },

    formatMessageAuthor(message) {
      if (!message.user_name) return 'Unknown User';
      
      if (message.user_type === 'schoolAdmin') {
        return message.user_name; // Already formatted as "Admin,account_nameEN"
      }
      
      // For students and advisors, split and capitalize each word
      return message.user_name
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    },

    async submitTodoComment() {
      if (!this.newTodoComment.trim()) {
        this.$message.warning('Please enter a comment');
        return;
      }

      try {
        const response = await postRequest('sendMessage', {
          from_id: this.selectedTodoId,
          type: 'todo',
          user_id: this.currentUserId,
          user_type: getUserType(),
          content: this.newTodoComment.trim(),
          attachement: [] // Optional: Add attachment handling if needed
        });

        if (response.data.code === 0) {
          this.$message.success('Comment added successfully');
          this.newTodoComment = ''; // Clear the input
          await this.fetchTodoMessages(this.selectedTodoId); // Refresh messages
        } else if (response.data.code === -2) {
          this.$message.error('Comments are closed for this todo');
        } else {
          this.$message.error(response.data.message || 'Failed to add comment');
        }
      } catch (error) {
        console.error('Error adding comment:', error);
        this.$message.error('Failed to add comment');
      }
    },
  }
}
</script>

<!-- ------------------------------------------ style ------------------------------------------ -->
<style scoped lang="scss">
.todos-container {
  display: flex;
  gap: 20px;
  height: calc(100vh - 250px);
  width: 100%;
  position: relative;
  overflow: hidden;
}

.todos-list {
  flex: 1;
  max-width: 600px;
  min-width: 300px;
  transition: all 0.3s ease;
  overflow-y: auto;
  padding-right: 10px;

  &.with-form {
    width: 600px;
  }

  &.with-detail {
    width: 600px;
  }
}

.todos-header {
  margin-bottom: 20px;
  display: flex;
  justify-content: flex-end;
}

.todos-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.todo-card {
  background: white;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  cursor: pointer;
  border: 1px solid #EBEEF5;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
  }

  &.selected {
    border-color: #409EFF;
    box-shadow: 0 2px 12px 0 rgba(64,158,255,0.1);
    
    .todo-title {
      color: #409EFF;
    }
  }
}

.todo-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.todo-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  flex: 1;
  margin-right: 12px;
}

.todo-description {
  color: #606266;
  font-size: 14px;
  margin-bottom: 16px;
  min-height: 40px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.todo-meta {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #909399;

  .todo-assignee, .todo-due-date {
    display: flex;
    align-items: center;
    gap: 8px;

    i {
      font-size: 14px;
    }
  }
}

.todo-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid #EBEEF5;

  .el-button {
    padding: 0;
    
    &:hover {
      color: #409EFF;
    }
  }
}

.el-tag {
  text-transform: capitalize;
  font-size: 12px;
}

:deep(.el-card__body) {
  padding: 16px;
}

.todo-form {
  width: 50%;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: -2px 0 4px rgba(0,0,0,0.1);
  height: fit-content;
  position: sticky;
  top: 20px;
  overflow-y: auto;
  max-height: calc(100vh - 120px);

  .form-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
    }
  }

  .form-footer {
    margin-top: 20px;
    text-align: right;
    
    .el-button {
      margin-left: 10px;
    }
  }
}

:deep(.el-form-item__label) {
  font-weight: 500;
}

:deep(.el-input__inner) {
  border-radius: 4px;
}

:deep(.el-textarea__inner) {
  border-radius: 4px;
}

.no-data {
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
  color: #909399;

  i {
    font-size: 48px;
    margin-bottom: 16px;
  }

  p {
    margin: 0;
    font-size: 14px;
  }
}

.todo-detail {
  width: 50%;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: -2px 0 4px rgba(0,0,0,0.1);
  height: fit-content;
  position: sticky;
  top: 20px;
  overflow-y: auto;
  max-height: calc(100vh - 120px);

  .detail-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    
    h2 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
    }

    .detail-actions {
      display: flex;
      gap: 8px;
      align-items: center;

      .el-button {
        margin-left: 0;  // Override element-ui default margin
      }
    }
  }

  .detail-section {
    margin-bottom: 20px;
    
    label {
      display: block;
      font-weight: 500;
      color: #606266;
      margin-bottom: 8px;
    }

    p {
      margin: 0;
      color: #303133;
      line-height: 1.5;
    }

    h3 {
      margin: 0 0 12px 0;
      font-size: 18px;
    }
  }
}

.todo-title-wrapper, .detail-title-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.todo-title, .detail-section h3 {
  &.completed {
    text-decoration: line-through;
    color: #909399;
  }
}

.todo-card-content {
  cursor: pointer;
}

:deep(.el-checkbox__input) {
  cursor: pointer;
  .el-checkbox__inner {
    border-radius: 2px;
    &::after {
      border-radius: 1px;
      height: 7px;
      left: 4px;
      top: 1px;
      width: 3px;
      border-width: 2px;
    }
  }
}

:deep(.el-checkbox__label) {
  display: none;
}

:deep(.el-checkbox__input.is-disabled) {
  cursor: not-allowed;
  
  .el-checkbox__inner {
    background-color: #F5F7FA;
    border-color: #E4E7ED;
    cursor: not-allowed;
  }
}

.detail-meta {
  display: flex;
  gap: 24px;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 8px;
  margin: 16px 0;

  .meta-item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    
    i {
      color: #909399;
      margin-top: 4px;
    }

    .meta-content {
      display: flex;
      flex-direction: column;
      gap: 4px;

      label {
        font-size: 12px;
        color: #909399;
        margin: 0;
      }

      span {
        color: #303133;
        font-size: 14px;
      }
    }
  }
}

.description-section {
  margin-top: 24px;
  
  label {
    font-size: 14px;
    margin-bottom: 12px;
  }

  p {
    background: #fff;
    padding: 12px;
    border-radius: 4px;
    margin: 0;
  }
}

.messages-section {
  margin-top: 30px;
  
  h3 {
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid #eee;
  }
}

.messages-list {
  max-height: 400px;
  overflow-y: auto;
  margin-bottom: 20px;
}

.message-item {
  padding: 15px;
  margin-bottom: 15px;
  background: #f9f9f9;
  border-radius: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.message-meta {
  margin-bottom: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .message-author {
    font-weight: 600;
    color: #333;
  }
  
  .message-time {
    font-size: 0.85em;
    color: #999;
  }
}

.message-content {
  color: #666;
  line-height: 1.5;
}

.no-messages {
  text-align: center;
  padding: 30px;
  color: #909399;
  font-style: italic;
}

.new-comment {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #eee;

  .comment-actions {
    text-align: right;
    margin-top: 10px;
  }
}

.message-author {
  &.admin {
    color: #409EFF; // Different color for admin users
  }
}

.admin-no-comment {
  text-align: center;
  padding: 15px;
  color: #909399;
  font-style: italic;
  background: #f5f7fa;
  border-radius: 4px;
}
</style>
